// ReportingModal — click a report row to submit. Imported by Reporting.jsx.
//
// Lives at home/modals.jsx for historical reasons (used to also host
// TaskDetailModal / MonitoringModal / RequestModal / HomeModals — those
// were removed 2026-05-08 alongside the tasks module). Uses ModalShell +
// the small style helpers below for consistent chrome.
//
// "Last reports" shows the most recent persisted submission_note +
// completion timestamp from the report_assignments row itself
// (migration 96 added last_submission_note). Full submission history
// is read from report_submissions and rendered inline.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { aws } from '../lib/awsdb';
import { authWrite } from '../lib/authWrite';
import HarryBox from '../ai/HarryBox';
import { storage } from '../lib/awsStorage';
import { AttachmentStrip } from '../lib/AttachmentStrip';

// Voice memo cleanup. STT now happens in-browser via the Web Speech API
// (no external key, no upload for transcription) — we capture text live as
// the user speaks. The audio file still gets uploaded as an attachment for
// audit/replay. Once recording ends, the accumulated transcript is POSTed
// here; the Lambda runs a Claude Haiku polish pass (filler removed, light
// structure, every fact preserved) and returns the cleaned prose for the
// textarea. Mo edits before submitting.
const TRANSCRIBE_URL = import.meta.env.VITE_AWS_TRANSCRIBE_REPORT_URL;

// Browser-native STT. Chrome / Safari / Edge expose this; Firefox doesn't.
// On unsupported browsers the Record button still records audio (saved as
// an attachment) but skips the auto-transcribe-into-textarea step.
const SpeechRecognitionCtor =
  typeof window !== 'undefined'
    ? (window.SpeechRecognition || window.webkitSpeechRecognition || null)
    : null;

// "0:42" / "12:05" — used in the recorder timer + the audio attachment label.
function formatMs(ms) {
  const total = Math.max(0, Math.floor((ms || 0) / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

// Toolbar buttons (Photo / Link / Record / Pause / Resume / Stop / Cancel).
// Same shape across all of them so the row stays visually steady when the
// recording panel swaps Pause↔Resume on the same slot.
const attachBtnStyle = {
  padding: '7px 12px', borderRadius: 8, cursor: 'pointer',
  background: 'rgba(255,255,255,0.06)',
  border: '1px solid rgba(255,255,255,0.14)',
  color: 'rgba(255,255,255,0.88)',
  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, system-ui, sans-serif',
  fontSize: 12, fontWeight: 500,
  display: 'inline-flex', alignItems: 'center', gap: 6,
};

const attachInputStyle = {
  width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 8,
  background: 'rgba(0,0,0,0.30)', border: '1px solid rgba(255,255,255,0.10)',
  color: 'rgba(255,255,255,0.95)',
  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, system-ui, sans-serif',
  fontSize: 12.5, outline: 'none',
};

// One-time keyframe injection — recording dot pulse + transcribing spinner.
// Idempotent so re-mounting the modal doesn't re-add the <style>.
function ensureRecordingKeyframes() {
  if (typeof document === 'undefined') return;
  const id = 'mc-reporting-modal-keyframes';
  if (document.getElementById(id)) return;
  const el = document.createElement('style');
  el.id = id;
  el.textContent = `
    @keyframes mc-pulse {
      0%   { box-shadow: 0 0 0 0    rgba(239,68,68,0.7); }
      70%  { box-shadow: 0 0 0 10px rgba(239,68,68,0); }
      100% { box-shadow: 0 0 0 0    rgba(239,68,68,0); }
    }
    @keyframes mc-spin { to { transform: rotate(360deg); } }
  `;
  document.head.appendChild(el);
}

const SERIF = '\'Tiempos\', Georgia, \'Times New Roman\', serif';
const SANS  = 'Inter, -apple-system, BlinkMacSystemFont, system-ui, sans-serif';
const MONO  = '\'JetBrains Mono\', ui-monospace, \'SF Mono\', Menlo, monospace';

// ── Shell ───────────────────────────────────────────────────────────────
function ModalShell({ onClose, eyebrow, title, subtitle, children, footer, width = 560 }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
        background: 'rgba(10,12,16,0.72)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: `min(${width}px, calc(100vw - 48px))`,
          maxHeight: 'calc(100vh - 64px)',
          background: 'linear-gradient(180deg, #1C242C 0%, #12181E 100%)',
          borderRadius: 18,
          boxShadow:
            '0 34px 90px -20px rgba(0,0,0,0.55), ' +
            '0 2px 0 rgba(255,255,255,0.06) inset, ' +
            '0 0 0 1px rgba(255,255,255,0.06) inset, ' +
            '0 -1px 0 rgba(0,0,0,0.3) inset',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
          border: '1px solid rgba(255,255,255,0.08)',
          color: 'rgba(255,255,255,0.92)',
        }}
      >
        <div style={{
          padding: '16px 20px 14px',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          background: 'linear-gradient(180deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0) 100%)',
          display: 'flex', alignItems: 'flex-start', gap: 12,
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            {eyebrow && (
              <div style={{
                fontFamily: MONO, fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase',
                color: 'rgba(255,255,255,0.5)', marginBottom: 8,
              }}>{eyebrow}</div>
            )}
            <h2 style={{
              margin: 0, fontFamily: SERIF, fontSize: 22, fontWeight: 400,
              lineHeight: 1.25, letterSpacing: '-0.01em',
              color: 'rgba(255,255,255,0.96)', textWrap: 'pretty',
            }}>{title}</h2>
            {subtitle && (
              <div style={{
                marginTop: 6, fontFamily: SANS, fontSize: 13,
                color: 'rgba(255,255,255,0.6)', lineHeight: 1.45,
              }}>{subtitle}</div>
            )}
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: 'rgba(255,255,255,0.55)', padding: 6, marginTop: -2,
              borderRadius: 6, lineHeight: 1,
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>

        <div style={{
          padding: '18px 20px 20px', overflowY: 'auto',
          display: 'flex', flexDirection: 'column', gap: 18,
        }}>{children}</div>

        {footer && (
          <div style={{
            padding: '12px 16px 14px 20px',
            borderTop: '1px solid rgba(255,255,255,0.08)',
            background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.15) 100%)',
            display: 'flex', alignItems: 'center', gap: 8,
          }}>{footer}</div>
        )}
      </div>
    </div>,
    document.body,
  );
}

function Btn({ kind = 'ghost', onClick, disabled, children, type = 'button' }) {
  const base = {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '7px 14px', borderRadius: 8,
    fontFamily: SANS, fontSize: 12.5, fontWeight: 500, letterSpacing: '-0.005em',
    cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1,
    border: '1px solid transparent', transition: 'background 120ms, color 120ms',
  };
  const variants = {
    primary: { background: 'rgba(255,255,255,0.92)', color: '#0F1419', borderColor: 'rgba(255,255,255,0.2)' },
    ghost:   { background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.9)', borderColor: 'rgba(255,255,255,0.12)' },
    quiet:   { background: 'transparent',            color: 'rgba(255,255,255,0.6)', borderColor: 'transparent' },
    danger:  { background: 'rgba(194,87,74,0.18)',   color: '#FFB8A8', borderColor: 'rgba(194,87,74,0.35)' },
  };
  return (
    <button type={type} onClick={onClick} disabled={disabled} style={{ ...base, ...variants[kind] }}>
      {children}
    </button>
  );
}

function SectionLabel({ children }) {
  return (
    <div style={{
      fontFamily: MONO, fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase',
      color: 'rgba(255,255,255,0.45)', marginBottom: 8,
    }}>{children}</div>
  );
}

function StatBlock({ label, value, color }) {
  return (
    <div style={{
      background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)',
      borderRadius: 10, padding: '10px 12px',
      display: 'flex', flexDirection: 'column', gap: 3,
    }}>
      <span style={{
        fontFamily: MONO, fontSize: 9.5, letterSpacing: '0.12em', textTransform: 'uppercase',
        color: 'rgba(255,255,255,0.45)',
      }}>{label}</span>
      <span style={{
        fontFamily: SERIF, fontSize: 17, fontWeight: 400,
        color: color || 'rgba(255,255,255,0.95)', letterSpacing: '-0.01em',
      }}>{value}</span>
    </div>
  );
}

// ── ReportingModal ──────────────────────────────────────────────────────
// Click a report row on Home → this modal. Two modes:
//   overview → stats + AI summary + scrollable history + "Submit report" CTA
//   compose  → notes textarea + Submit button
//
// Submit writes a row into report_submissions (mig 97) AND bumps the
// streak / last_completed_at on report_assignments. The history list
// reads back from report_submissions so staff see every past submission
// when filing a new one — long bodies wrap (overflowWrap: anywhere).
//
// AI summary: hits the assistant-chat Lambda with a one-shot prompt
// containing recent submissions and asks for a week-on-week summary.
// Auto-runs the first time the modal opens with >= 2 submissions; user
// can hit Refresh to regenerate.
const CHAT_URL = import.meta.env.VITE_AWS_ASSISTANT_CHAT_URL;

export function ReportingModal({ report, session, onClose, onSubmitted, onAfterSubmit }) {
  const [mode, setMode] = useState('overview');
  const [draft, setDraft] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [history, setHistory] = useState([]);
  const [historyAttachments, setHistoryAttachments] = useState({}); // {submission_id: [att rows]}
  const [historyLoading, setHistoryLoading] = useState(true);
  const [summary, setSummary] = useState('');
  const [summarising, setSummarising] = useState(false);
  const [summaryErr, setSummaryErr] = useState('');
  const autoRunRef = useRef(false);

  // Attachments (photos / links / voice memos) live alongside the body.
  // Each item has shape { tempId, kind, path?, link_url?, label, mime,
  // size, duration_ms, uploadStatus: 'uploading'|'done'|'error', errorMsg? }.
  const [attachments, setAttachments] = useState([]);
  const [linkInputOpen, setLinkInputOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkLabel, setLinkLabel] = useState('');
  const [recordingState, setRecordingState] = useState('idle'); // 'idle'|'recording'|'paused'|'transcribing'
  const [recordingMs, setRecordingMs] = useState(0);
  const [transcribeError, setTranscribeError] = useState('');
  // Live transcript shown to the user during recording — interim segments
  // (browser is still settling on the words) appear dimmer than committed.
  const [interimTranscript, setInterimTranscript] = useState('');

  // Recording machinery — refs so we don't re-trigger renders on the
  // hot path. recordPausedMs accumulates total ms spent paused so the
  // visible timer represents real audio length.
  const mediaRecorderRef = useRef(null);
  const micStreamRef = useRef(null);
  const chunksRef = useRef([]);
  const recordStartRef = useRef(0);
  const recordPausedMsRef = useRef(0);
  const pauseStartRef = useRef(0);
  const tickerRef = useRef(null);
  const fileInputRef = useRef(null);
  // Speech recognition: live STT runs alongside the MediaRecorder. The
  // transcript buffer accumulates final segments across pause/resume
  // cycles; the recognition instance is recreated on each resume because
  // SpeechRecognition has no native pause.
  const recognitionRef = useRef(null);
  const transcriptBufferRef = useRef('');

  // Stable per-modal-open uuid so all uploads land under the same S3
  // prefix. We don't know the eventual submission_id until the user
  // hits Submit — the prefix only needs to be unique to this draft.
  const draftIdRef = useRef(null);
  if (draftIdRef.current == null) {
    try { draftIdRef.current = crypto.randomUUID(); }
    catch { draftIdRef.current = `draft-${Date.now()}-${Math.random().toString(36).slice(2)}`; }
  }

  const cadenceLabel = report.cadence === 'custom'
    ? `every ${report.cadence_days || '?'} days`
    : (report.cadence || 'unspecified');

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    const { data } = await aws.from('report_submissions')
      .select('*').eq('assignment_id', report.id)
      .order('submitted_at', { ascending: false }).limit(20);
    setHistory(data || []);

    // Fetch attachments for the loaded submissions in one round-trip.
    const ids = (data || []).map(s => s.id);
    if (ids.length > 0) {
      const { data: atts } = await aws.from('report_submission_attachments')
        .select('*').in('submission_id', ids).order('uploaded_at', { ascending: true });
      const grouped = {};
      for (const a of atts || []) (grouped[a.submission_id] ||= []).push(a);
      setHistoryAttachments(grouped);
    } else {
      setHistoryAttachments({});
    }

    setHistoryLoading(false);
  }, [report.id]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  async function runSummary() {
    if (!CHAT_URL || summarising || history.length < 2) return;
    setSummarising(true);
    setSummaryErr('');
    try {
      const recent = history.slice(0, 8).reverse(); // chronological for the LLM
      const lines = recent.map(s => {
        const ts = new Date(s.submitted_at).toLocaleDateString('en-AU', {
          day: '2-digit', month: 'short', year: '2-digit',
        });
        return `[${ts}] ${s.body}`;
      }).join('\n\n');
      const prompt =
        `You are summarising recurring report submissions for "${report.title}". ` +
        `Below are the last ${recent.length} submissions in chronological order. ` +
        `Write a 2-3 sentence summary of how things have evolved week-on-week — ` +
        `numbers if mentioned, recurring themes, anything trending up or down. ` +
        `Be direct, no preamble, no bullet points.\n\n${lines}`;
      const res = await fetch(CHAT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question: prompt,
          context: '',
          userPin: session?.code,
          conversationHistory: [],
          model: 'claude-sonnet-4-6',
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setSummary((data.text || '').trim() || 'No summary generated.');
    } catch (e) {
      setSummaryErr(e.message || 'Summary failed');
    } finally {
      setSummarising(false);
    }
  }

  // Auto-summarise once per open when we have something to compare.
  useEffect(() => {
    if (autoRunRef.current || historyLoading || history.length < 2) return;
    autoRunRef.current = true;
    runSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyLoading, history.length]);

  // Stop any in-flight recording + release the mic when the modal closes
  // or unmounts. Without this, a fast close keeps the mic light on.
  // Same effect injects the keyframe styles on mount (idempotent).
  useEffect(() => {
    ensureRecordingKeyframes();
    return () => {
      try { mediaRecorderRef.current?.stop?.(); } catch { /* already stopped */ }
      try { recognitionRef.current?.stop?.(); } catch { /* already stopped */ }
      micStreamRef.current?.getTracks?.().forEach(t => t.stop());
      if (tickerRef.current) clearInterval(tickerRef.current);
    };
  }, []);

  // ── Photo attachments ──────────────────────────────────────────────
  function openPhotoPicker() { fileInputRef.current?.click(); }

  async function handlePhotoSelected(e) {
    const files = Array.from(e.target.files || []);
    e.target.value = '';                                   // allow re-pick of same file
    for (const file of files) {
      if (!file.type.startsWith('image/')) continue;
      const tempId = crypto.randomUUID?.() || `t-${Date.now()}-${Math.random()}`;
      const filename = file.name || `photo-${Date.now()}.jpg`;
      const subPath = `${draftIdRef.current}/${Date.now()}-${filename}`;
      setAttachments(prev => [...prev, {
        tempId, kind: 'photo', label: filename, mime: file.type,
        size: file.size, uploadStatus: 'uploading',
      }]);
      try {
        const { data, error } = await storage.from('report-attachments').upload(
          subPath, file, { contentType: file.type },
        );
        if (error) throw new Error(error.message || 'upload failed');
        const path = String(data.path || '').replace(/^s3:/, '');
        setAttachments(prev => prev.map(a =>
          a.tempId === tempId ? { ...a, path, uploadStatus: 'done' } : a,
        ));
      } catch (err) {
        setAttachments(prev => prev.map(a =>
          a.tempId === tempId ? { ...a, uploadStatus: 'error', errorMsg: err.message } : a,
        ));
      }
    }
  }

  // ── Link attachments ───────────────────────────────────────────────
  function commitLink() {
    const url = linkUrl.trim();
    if (!url) return;
    // Light schema sanity — let the user paste 'docs.google.com/...' too.
    const normalised = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    const tempId = crypto.randomUUID?.() || `t-${Date.now()}-${Math.random()}`;
    setAttachments(prev => [...prev, {
      tempId, kind: 'link',
      link_url: normalised,
      label: linkLabel.trim() || normalised,
      uploadStatus: 'done',
    }]);
    setLinkUrl('');
    setLinkLabel('');
    setLinkInputOpen(false);
  }

  function removeAttachment(tempId) {
    setAttachments(prev => prev.filter(a => a.tempId !== tempId));
  }

  // ── Voice recording ────────────────────────────────────────────────
  // MediaRecorder pause/resume is supported on all current desktop and
  // mobile browsers (Safari 14.1+, Chrome ever, Firefox ever). If the
  // browser can't construct a MediaRecorder we surface an error and
  // never enter the recording state.

  // Spin up a fresh SpeechRecognition session. Called by start() and
  // resume() — SR has no native pause, so each pause/resume cycle creates
  // a new instance and its `onresult` events append into the same buffer.
  function startSpeechRecognition() {
    if (!SpeechRecognitionCtor) return null;
    let r;
    try { r = new SpeechRecognitionCtor(); }
    catch { return null; }
    r.continuous = true;
    r.interimResults = true;
    r.lang = 'en-AU';
    r.onresult = (e) => {
      let interim = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const seg = e.results[i][0]?.transcript || '';
        if (e.results[i].isFinal) {
          const next = (transcriptBufferRef.current + ' ' + seg).replace(/\s+/g, ' ').trim();
          transcriptBufferRef.current = next;
        } else {
          interim += seg;
        }
      }
      setInterimTranscript(interim.trim());
    };
    r.onerror = (e) => {
      // 'no-speech' is normal on a long pause; don't surface it.
      if (e?.error && e.error !== 'no-speech' && e.error !== 'aborted') {
        console.warn('SpeechRecognition error:', e.error);
      }
    };
    r.onend = () => { setInterimTranscript(''); };
    try { r.start(); }
    catch (err) {
      console.warn('SpeechRecognition.start failed:', err);
      return null;
    }
    return r;
  }

  // Stop the current SR instance and resolve once `onend` has fired so
  // any in-progress segment has been finalised into the buffer.
  function stopSpeechRecognitionAndWait() {
    return new Promise(resolve => {
      const r = recognitionRef.current;
      if (!r) return resolve();
      const prevOnEnd = r.onend;
      r.onend = (e) => { try { prevOnEnd?.(e); } catch { /* */ } resolve(); };
      try { r.stop(); }
      catch { resolve(); }
    });
  }

  function pickRecMime() {
    const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'];
    for (const m of candidates) {
      if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported?.(m)) return m;
    }
    return '';
  }

  async function startRecording() {
    setTranscribeError('');
    if (typeof MediaRecorder === 'undefined') {
      setTranscribeError('Voice recording isn’t supported in this browser.');
      return;
    }
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e) {
      setTranscribeError('Microphone access denied — allow it in your browser settings to record.');
      return;
    }
    micStreamRef.current = stream;
    const mime = pickRecMime();
    let rec;
    try {
      rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
    } catch (e) {
      stream.getTracks().forEach(t => t.stop());
      micStreamRef.current = null;
      setTranscribeError('Could not start recorder — ' + (e.message || 'unknown error'));
      return;
    }
    chunksRef.current = [];
    rec.ondataavailable = (ev) => {
      if (ev.data && ev.data.size > 0) chunksRef.current.push(ev.data);
    };
    rec.start();
    mediaRecorderRef.current = rec;
    recordStartRef.current = Date.now();
    recordPausedMsRef.current = 0;
    setRecordingMs(0);
    setRecordingState('recording');
    if (tickerRef.current) clearInterval(tickerRef.current);
    tickerRef.current = setInterval(() => {
      setRecordingMs(Date.now() - recordStartRef.current - recordPausedMsRef.current);
    }, 250);

    // Reset the transcript buffer for this take and kick off live STT.
    // On unsupported browsers the audio still records; we just skip the
    // auto-fill and the user types the body.
    transcriptBufferRef.current = '';
    setInterimTranscript('');
    recognitionRef.current = startSpeechRecognition();
  }

  function pauseRecording() {
    const rec = mediaRecorderRef.current;
    if (!rec || rec.state !== 'recording') return;
    rec.pause();
    pauseStartRef.current = Date.now();
    setRecordingState('paused');
    if (tickerRef.current) { clearInterval(tickerRef.current); tickerRef.current = null; }
    // Stop SR — the buffer survives in transcriptBufferRef across pause.
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch { /* */ }
      recognitionRef.current = null;
    }
  }

  function resumeRecording() {
    const rec = mediaRecorderRef.current;
    if (!rec || rec.state !== 'paused') return;
    rec.resume();
    recordPausedMsRef.current += Date.now() - pauseStartRef.current;
    setRecordingState('recording');
    if (tickerRef.current) clearInterval(tickerRef.current);
    tickerRef.current = setInterval(() => {
      setRecordingMs(Date.now() - recordStartRef.current - recordPausedMsRef.current);
    }, 250);
    // Spin up a fresh SR session — its results append to the same buffer.
    recognitionRef.current = startSpeechRecognition();
  }

  async function stopAndTranscribe() {
    const rec = mediaRecorderRef.current;
    if (!rec) return;
    if (tickerRef.current) { clearInterval(tickerRef.current); tickerRef.current = null; }

    const finalMs = recordingMs;
    const recMime = rec.mimeType || 'audio/webm';

    // 1) Stop both: MediaRecorder (audio file) AND SpeechRecognition
    //    (transcript). Wait for both to fully flush before reading.
    const audioStopped = new Promise(resolve => {
      rec.addEventListener('stop', resolve, { once: true });
      try { rec.stop(); } catch { resolve(); }
    });
    const recogStopped = stopSpeechRecognitionAndWait();
    await Promise.all([audioStopped, recogStopped]);
    recognitionRef.current = null;
    micStreamRef.current?.getTracks?.().forEach(t => t.stop());
    micStreamRef.current = null;
    mediaRecorderRef.current = null;

    const blob = new Blob(chunksRef.current, { type: recMime });
    chunksRef.current = [];
    const rawTranscript = transcriptBufferRef.current.trim();

    if (blob.size === 0 && !rawTranscript) {
      setRecordingState('idle');
      setRecordingMs(0);
      setTranscribeError('No audio or speech captured — try again.');
      return;
    }

    setRecordingState('transcribing');

    // 2) Upload audio for the attachment record (best-effort — transcription
    //    succeeds even if the upload fails). Audio is stored separately so
    //    anyone reading the report can re-listen if a number looks off.
    let audioPath = null;
    if (blob.size > 0) {
      const ext = recMime.includes('mp4') ? 'mp4'
        : recMime.includes('ogg') ? 'ogg'
        : 'webm';
      const filename = `voice-${Date.now()}.${ext}`;
      const subPath = `${draftIdRef.current}/${filename}`;
      try {
        const { data: upData, error: upErr } = await storage.from('report-attachments').upload(
          subPath, blob, { contentType: recMime },
        );
        if (upErr) throw new Error(upErr.message || 'audio upload failed');
        audioPath = String(upData.path || '').replace(/^s3:/, '');
      } catch (err) {
        console.warn('audio attachment upload failed:', err.message || err);
        // Non-fatal; we still proceed to transcription + draft insert.
      }
    }

    // 3) Hand the transcript to the Lambda for the Haiku polish pass.
    //    Fallback chain: if SR captured nothing OR the Lambda fails, we
    //    still drop the raw transcript into the textarea so the user
    //    isn't left empty-handed.
    let textToInsert = rawTranscript;
    if (rawTranscript) {
      try {
        if (!TRANSCRIBE_URL) throw new Error('Transcribe URL not configured');
        const res = await fetch(TRANSCRIBE_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ transcript: rawTranscript, userPin: session?.code }),
        });
        if (!res.ok) {
          const t = await res.text().catch(() => '');
          throw new Error(t || `cleanup ${res.status}`);
        }
        const result = await res.json();
        if (result.error) throw new Error(result.error);
        const cleaned = String(result.cleaned || '').trim();
        if (cleaned) textToInsert = cleaned;
      } catch (err) {
        console.warn('transcript cleanup failed; using raw:', err.message);
        // Surface a hint but still insert raw — Mo can clean by hand.
        setTranscribeError('Auto-cleanup failed — raw transcript inserted.');
      }
    } else if (audioPath) {
      // Recording succeeded but STT didn't (Firefox or denied mic).
      setTranscribeError('Live transcription isn’t supported in this browser — audio attached, type the body manually.');
    }

    if (textToInsert) {
      setDraft(prev => prev ? `${prev}\n\n${textToInsert}` : textToInsert);
    }

    // 4) Add the audio attachment chip if we have an audio file.
    if (audioPath) {
      const tempId = crypto.randomUUID?.() || `t-${Date.now()}-${Math.random()}`;
      setAttachments(prev => [...prev, {
        tempId, kind: 'audio',
        path: audioPath,
        label: `voice memo (${formatMs(finalMs)})`,
        mime: recMime,
        size: blob.size,
        duration_ms: finalMs,
        uploadStatus: 'done',
      }]);
    }

    transcriptBufferRef.current = '';
    setInterimTranscript('');
    setRecordingState('idle');
    setRecordingMs(0);
  }

  function cancelRecording() {
    const rec = mediaRecorderRef.current;
    if (rec && rec.state !== 'inactive') {
      try { rec.stop(); } catch { /* */ }
    }
    if (tickerRef.current) { clearInterval(tickerRef.current); tickerRef.current = null; }
    micStreamRef.current?.getTracks?.().forEach(t => t.stop());
    micStreamRef.current = null;
    mediaRecorderRef.current = null;
    chunksRef.current = [];
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch { /* */ }
      recognitionRef.current = null;
    }
    transcriptBufferRef.current = '';
    setInterimTranscript('');
    setRecordingState('idle');
    setRecordingMs(0);
    setTranscribeError('');
  }

  // Block submit while a photo or voice memo is mid-flight — otherwise
  // the row would land without its attachment(s).
  const uploadInFlight = attachments.some(a => a.uploadStatus === 'uploading')
    || recordingState === 'recording'
    || recordingState === 'paused'
    || recordingState === 'transcribing';

  async function submit() {
    const body = draft.trim();
    if (!body || submitting || uploadInFlight) return;
    setSubmitting(true);
    setError('');
    try {
      // Two writes: append to history table, then bump streak on the
      // assignment row. Both go through authWrite for the audit log.
      // Capture the inserted row so we can hand the new submission_id to
      // the caller (Reporting.jsx fires the report-extract Lambda with it).
      const inserted = await authWrite(session, 'report_submissions', 'insert', {
        assignment_id: report.id,
        body,
        submitted_by: session?.code || null,
      });
      await authWrite(session, 'report_assignments', 'update', {
        last_completed_at: new Date().toISOString(),
        streak: (report.streak || 0) + 1,
      }, { column: 'id', value: report.id });
      const submissionId = Array.isArray(inserted) ? inserted[0]?.id : inserted?.id;

      // Persist attachments. Direct PostgREST — table not in authWrite's
      // allow list (and uploaded_by captures the audit). Non-blocking on
      // failure: the submission already landed.
      const ready = attachments.filter(a => a.uploadStatus === 'done');
      if (submissionId && ready.length > 0) {
        const rows = ready.map(a => ({
          submission_id: submissionId,
          kind: a.kind,
          path: a.path || null,
          link_url: a.link_url || null,
          label: a.label || null,
          mime_type: a.mime || null,
          size_bytes: a.size || null,
          duration_ms: a.duration_ms || null,
          uploaded_by: session?.code || null,
        }));
        try { await aws.from('report_submission_attachments').insert(rows); }
        catch (e) { console.warn('attachment insert failed:', e?.message || e); }
      }

      onAfterSubmit?.(submissionId);
      onSubmitted?.();
      onClose?.();
    } catch (e) {
      setError(e.message || 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <ModalShell
      onClose={onClose}
      eyebrow={<>Report · {report.source || cadenceLabel}</>}
      title={report.title}
      subtitle={report.description || null}
      footer={mode === 'overview' ? (
        <>
          <Btn kind="primary" onClick={() => setMode('compose')}>Submit report</Btn>
          <Btn kind="ghost" onClick={onClose}>Close</Btn>
        </>
      ) : (
        <>
          <Btn kind="primary" onClick={submit} disabled={!draft.trim() || submitting || uploadInFlight}>
            {submitting ? 'Submitting…'
              : uploadInFlight ? (recordingState !== 'idle' ? 'Finish recording first…' : 'Uploading…')
              : 'Submit'}
          </Btn>
          <Btn kind="ghost" onClick={() => setMode('overview')}>Back</Btn>
        </>
      )}
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
        <StatBlock label="Cadence" value={cadenceLabel} />
        <StatBlock label="Streak" value={`${report.streak || 0} runs`} />
        <StatBlock label="Submissions" value={historyLoading ? '…' : String(history.length)} />
      </div>

      {mode === 'compose' && (
        <div>
          <SectionLabel>Your report</SectionLabel>
          <textarea
            autoFocus
            value={draft}
            onChange={e => setDraft(e.target.value)}
            placeholder="What happened this cycle? Numbers, blockers, what to flag…"
            rows={6}
            style={{
              width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 10,
              background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.1)',
              color: 'rgba(255,255,255,0.95)', fontFamily: SANS, fontSize: 13,
              lineHeight: 1.5, outline: 'none', resize: 'vertical', minHeight: 120,
            }}
          />

          {/* Hidden picker fed by the Photo button. Multiple lets Mo
              attach two snaps at once (e.g. before/after of a shelf). */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: 'none' }}
            onChange={handlePhotoSelected}
          />

          {/* Toolbar (idle state). When a recording session is active
              the toolbar is replaced by the recording panel below. */}
          {recordingState === 'idle' && (
            <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
              <button type="button" onClick={openPhotoPicker} style={attachBtnStyle}>
                <span aria-hidden style={{ fontSize: 13 }}>📎</span> Photo
              </button>
              <button type="button" onClick={() => setLinkInputOpen(v => !v)} style={attachBtnStyle}>
                <span aria-hidden style={{ fontSize: 13 }}>🔗</span> Link
              </button>
              <button type="button" onClick={startRecording} style={{
                ...attachBtnStyle,
                background: 'linear-gradient(135deg, rgba(220,38,38,0.14), rgba(220,38,38,0.06))',
                borderColor: 'rgba(220,38,38,0.30)',
                color: '#FFD0C8',
              }}>
                <span aria-hidden style={{ fontSize: 13 }}>🎤</span> Record the update
              </button>
            </div>
          )}

          {/* Link input — inline expander triggered by the Link button. */}
          {linkInputOpen && recordingState === 'idle' && (
            <div style={{
              marginTop: 10, padding: '10px 12px', borderRadius: 10,
              background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.12)',
              display: 'flex', flexDirection: 'column', gap: 6,
            }}>
              <input
                autoFocus
                placeholder="https://…"
                value={linkUrl}
                onChange={e => setLinkUrl(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); commitLink(); } }}
                style={attachInputStyle}
              />
              <input
                placeholder="Optional label (e.g. “Q1 retention deck”)"
                value={linkLabel}
                onChange={e => setLinkLabel(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); commitLink(); } }}
                style={attachInputStyle}
              />
              <div style={{ display: 'flex', gap: 6 }}>
                <Btn kind="primary" onClick={commitLink} disabled={!linkUrl.trim()}>Add link</Btn>
                <Btn kind="ghost" onClick={() => { setLinkInputOpen(false); setLinkUrl(''); setLinkLabel(''); }}>Cancel</Btn>
              </div>
            </div>
          )}

          {/* Recording panel — shown during recording/paused/transcribing.
              Pause and Resume swap on the same button slot so the layout
              never jitters. The Cancel button discards the in-flight take.
              The live transcript area below shows committed words + the
              interim segment (dimmer) so the user sees STT working. */}
          {recordingState !== 'idle' && (
            <div style={{
              marginTop: 10, padding: '12px 14px', borderRadius: 12,
              background: recordingState === 'recording'
                ? 'linear-gradient(135deg, rgba(220,38,38,0.16), rgba(220,38,38,0.06))'
                : 'linear-gradient(135deg, rgba(124,58,237,0.14), rgba(124,58,237,0.05))',
              border: `1px solid ${recordingState === 'recording' ? 'rgba(220,38,38,0.32)' : 'rgba(124,58,237,0.30)'}`,
              display: 'flex', flexDirection: 'column', gap: 10,
            }}>
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                gap: 12, flexWrap: 'wrap',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  {recordingState === 'recording' && (
                    <span style={{
                      width: 10, height: 10, borderRadius: '50%', background: '#EF4444',
                      boxShadow: '0 0 0 0 rgba(239,68,68,0.7)',
                      animation: 'mc-pulse 1.4s ease-out infinite',
                    }} />
                  )}
                  {recordingState === 'paused' && (
                    <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)' }}>⏸</span>
                  )}
                  {recordingState === 'transcribing' && (
                    <span style={{
                      width: 12, height: 12, borderRadius: '50%',
                      border: '2px solid rgba(255,255,255,0.25)',
                      borderTopColor: 'rgba(255,255,255,0.85)',
                      animation: 'mc-spin 0.8s linear infinite',
                    }} />
                  )}
                  <span style={{
                    fontFamily: MONO, fontSize: 12,
                    color: 'rgba(255,255,255,0.9)', letterSpacing: '0.04em',
                  }}>
                    {recordingState === 'transcribing'
                      ? 'Cleaning up…'
                      : `${recordingState === 'paused' ? 'Paused · ' : ''}${formatMs(recordingMs)}`}
                  </span>
                </div>
                {recordingState !== 'transcribing' && (
                  <div style={{ display: 'flex', gap: 6 }}>
                    {recordingState === 'recording' ? (
                      <button type="button" onClick={pauseRecording} style={attachBtnStyle}>⏸ Pause</button>
                    ) : (
                      <button type="button" onClick={resumeRecording} style={attachBtnStyle}>▶ Resume</button>
                    )}
                    <button type="button" onClick={stopAndTranscribe} style={{
                      ...attachBtnStyle,
                      background: 'rgba(34,197,94,0.18)',
                      borderColor: 'rgba(34,197,94,0.36)',
                      color: '#C8F1D6',
                    }}>⏹ Stop &amp; transcribe</button>
                    <button type="button" onClick={cancelRecording} style={{
                      ...attachBtnStyle, color: 'rgba(255,255,255,0.55)',
                    }}>Cancel</button>
                  </div>
                )}
              </div>

              {/* Live transcript (committed + interim). Empty until SR
                  fires its first result; on unsupported browsers it stays
                  empty and the user just sees the timer. */}
              {(transcriptBufferRef.current || interimTranscript) && (
                <div style={{
                  padding: '8px 10px', borderRadius: 8,
                  background: 'rgba(0,0,0,0.28)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  fontFamily: SANS, fontSize: 12.5, lineHeight: 1.5,
                  color: 'rgba(255,255,255,0.9)',
                  maxHeight: 160, overflowY: 'auto',
                  whiteSpace: 'pre-wrap', overflowWrap: 'anywhere',
                }}>
                  <span>{transcriptBufferRef.current}</span>
                  {interimTranscript && (
                    <>
                      {transcriptBufferRef.current ? ' ' : ''}
                      <span style={{ color: 'rgba(255,255,255,0.5)', fontStyle: 'italic' }}>
                        {interimTranscript}
                      </span>
                    </>
                  )}
                </div>
              )}
              {!SpeechRecognitionCtor && recordingState === 'recording' && (
                <div style={{
                  fontFamily: MONO, fontSize: 10, letterSpacing: '0.04em',
                  color: 'rgba(255,255,255,0.5)',
                }}>
                  Live transcription isn’t supported in this browser — audio will still attach when you stop.
                </div>
              )}
            </div>
          )}

          {transcribeError && (
            <div style={{
              marginTop: 8, padding: '8px 10px', borderRadius: 8,
              background: 'rgba(194,87,74,0.18)', border: '1px solid rgba(194,87,74,0.35)',
              color: '#FFB8A8', fontFamily: SANS, fontSize: 12,
            }}>{transcribeError}</div>
          )}

          {/* Attachment chips. Each row shows kind + label + status,
              with × to drop. Photos uploading show a subtle dim. */}
          {attachments.length > 0 && (
            <ul style={{
              listStyle: 'none', margin: '10px 0 0', padding: 0,
              display: 'flex', flexDirection: 'column', gap: 6,
            }}>
              {attachments.map(a => (
                <li key={a.tempId} style={{
                  padding: '8px 10px', borderRadius: 10,
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  display: 'flex', alignItems: 'center', gap: 10,
                  opacity: a.uploadStatus === 'uploading' ? 0.6 : 1,
                }}>
                  <span style={{ fontSize: 13 }}>
                    {a.kind === 'photo' ? '📷' : a.kind === 'audio' ? '🎤' : '🔗'}
                  </span>
                  <span style={{
                    flex: 1, minWidth: 0, fontFamily: SANS, fontSize: 12.5,
                    color: 'rgba(255,255,255,0.88)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>{a.label}</span>
                  {a.uploadStatus === 'uploading' && (
                    <span style={{ fontFamily: MONO, fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>uploading…</span>
                  )}
                  {a.uploadStatus === 'error' && (
                    <span style={{ fontFamily: MONO, fontSize: 10, color: '#FFB8A8' }}>{a.errorMsg || 'failed'}</span>
                  )}
                  <button
                    type="button"
                    onClick={() => removeAttachment(a.tempId)}
                    title="Remove"
                    style={{
                      background: 'transparent', border: 'none', cursor: 'pointer',
                      color: 'rgba(255,255,255,0.55)', fontSize: 14, padding: '0 2px',
                    }}
                  >×</button>
                </li>
              ))}
            </ul>
          )}

          {error && (
            <div style={{
              marginTop: 8, padding: '8px 10px', borderRadius: 8,
              background: 'rgba(194,87,74,0.18)', border: '1px solid rgba(194,87,74,0.35)',
              color: '#FFB8A8', fontFamily: SANS, fontSize: 12,
            }}>{error}</div>
          )}
        </div>
      )}

      {mode === 'overview' && history.length >= 2 && (
        <div>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 8 }}>
            <SectionLabel>AI summary · week-on-week</SectionLabel>
            <button onClick={runSummary} disabled={summarising} style={{
              background: 'transparent', border: 'none', cursor: summarising ? 'wait' : 'pointer',
              color: 'rgba(255,255,255,0.5)', fontFamily: MONO, fontSize: 10,
              letterSpacing: '0.06em', textTransform: 'uppercase', padding: 0,
            }}>{summarising ? 'thinking…' : 'refresh'}</button>
          </div>
          <div style={{
            padding: '12px 14px', borderRadius: 10,
            background: 'linear-gradient(135deg, rgba(124,58,237,0.10), rgba(124,58,237,0.04))',
            border: '1px solid rgba(124,58,237,0.25)',
            fontFamily: SERIF, fontSize: 14, color: 'rgba(255,255,255,0.92)',
            lineHeight: 1.55, whiteSpace: 'pre-wrap',
            overflowWrap: 'anywhere', wordBreak: 'break-word',
          }}>
            {summary ? summary : (
              summarising ? 'Summarising…' :
              summaryErr ? <span style={{ color: '#FFB8A8', fontFamily: SANS, fontSize: 12 }}>Couldn’t summarise — {summaryErr}</span> :
              <span style={{ color: 'rgba(255,255,255,0.5)', fontFamily: SANS, fontSize: 12 }}>No summary yet.</span>
            )}
          </div>
        </div>
      )}

      <div>
        <SectionLabel>Submission history</SectionLabel>
        {historyLoading ? (
          <div style={{
            padding: '14px', borderRadius: 10,
            background: 'rgba(255,255,255,0.04)', border: '1px dashed rgba(255,255,255,0.1)',
            fontFamily: SANS, fontSize: 12, color: 'rgba(255,255,255,0.45)',
          }}>Loading…</div>
        ) : history.length === 0 ? (
          <div style={{
            padding: '14px', borderRadius: 10,
            background: 'rgba(255,255,255,0.04)', border: '1px dashed rgba(255,255,255,0.1)',
            fontFamily: SANS, fontSize: 12, color: 'rgba(255,255,255,0.45)',
          }}>No submissions yet — this will be the first.</div>
        ) : (
          <ul style={{
            listStyle: 'none', margin: 0, padding: 0,
            display: 'flex', flexDirection: 'column', gap: 8,
            maxHeight: 280, overflowY: 'auto',
          }}>
            {history.map(s => {
              const ts = new Date(s.submitted_at).toLocaleString('en-AU', {
                day: '2-digit', month: 'short', year: '2-digit',
                hour: '2-digit', minute: '2-digit',
              });
              return (
                <li key={s.id} style={{
                  padding: '10px 12px', borderRadius: 10,
                  background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)',
                  display: 'flex', flexDirection: 'column', gap: 6,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <span style={{
                      fontFamily: MONO, fontSize: 10, letterSpacing: '0.06em',
                      color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase',
                    }}>{ts}</span>
                    {s.submitted_by && (
                      <span style={{
                        fontFamily: MONO, fontSize: 10, letterSpacing: '0.04em',
                        color: 'rgba(255,255,255,0.4)',
                      }}>{s.submitted_by}</span>
                    )}
                  </div>
                  <span style={{
                    fontFamily: SANS, fontSize: 13, color: 'rgba(255,255,255,0.88)',
                    lineHeight: 1.5,
                    whiteSpace: 'pre-wrap',
                    overflowWrap: 'anywhere', wordBreak: 'break-word',
                  }}>{s.body}</span>
                  <AttachmentStrip attachments={historyAttachments[s.id]} theme="dark" />
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </ModalShell>
  );
}

